/* ==========================================================================
   Clapstore motion layer.

   The "buttery" feel is not a scroll-hijacking library. It comes from four
   things, all of which are done here:

     1. Only ever animate transform and opacity. Never top/left/width/height —
        those hit layout and paint on every frame and are what makes a site
        feel sticky.
     2. Every scroll listener is passive and rAF-throttled, so the compositor
        is never blocked.
     3. Images fade in only after decode() resolves, so you never see a
        half-painted image snap into place.
     4. All of it turns off completely under prefers-reduced-motion.

   Deliberately no smooth-scroll library. Hijacking native scroll momentum is
   what makes sites feel worse on real phones, not better.
   ========================================================================== */
(function () {
  'use strict';

  /* FIRST, before anything that can throw. Every hidden-until-revealed rule in
     the theme is gated on this class, so the page is only allowed to hide
     things once we know the script that un-hides them is running. */
  document.documentElement.classList.add('cs-js');

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------------
     1. Reveal on scroll — staggered within a section, one observer total.
     --------------------------------------------------------------------- */
  function initReveal() {
    var els = document.querySelectorAll('.cs-reveal:not(.is-in)');
    if (!els.length) return;

    if (reduced || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        // Stagger siblings so a grid cascades instead of popping as one block.
        var i = parseInt(el.dataset.csStagger || '0', 10);
        el.style.transitionDelay = Math.min(i * 60, 300) + 'ms';
        el.classList.add('is-in');
        io.unobserve(el);
      });
    }, {
      // Fire slightly before the element enters view so the transition has
      // already begun by the time it is actually on screen.
      rootMargin: '0px 0px -12% 0px',
      threshold: 0.01
    });

    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------------------------------------------------------------------
     2. Image fade-in after decode — no half-painted snap.
     --------------------------------------------------------------------- */
  function initImages() {
    document.querySelectorAll('img.cs-fade:not(.is-loaded)').forEach(function (img) {
      var show = function () { img.classList.add('is-loaded'); };
      if (img.complete && img.naturalWidth) {
        (img.decode ? img.decode().catch(function () {}) : Promise.resolve()).then(show);
      } else {
        img.addEventListener('load', function () {
          (img.decode ? img.decode().catch(function () {}) : Promise.resolve()).then(show);
        }, { once: true });
        img.addEventListener('error', show, { once: true });
      }
    });
  }

  /* ---------------------------------------------------------------------
     3. Header scroll behaviour — deliberately NOT implemented here.

     Dawn already ships a <sticky-header> custom element (see sections/header.liquid)
     with an on-scroll-up reveal mode. Running a second scroll handler against the
     same element would mean two systems writing conflicting transforms on the same
     frame — the classic cause of a header that stutters or flickers.

     The theme uses Dawn's native implementation, configured to 'on-scroll-up'.
     --------------------------------------------------------------------- */

  /* ---------------------------------------------------------------------
     4. Horizontal rails — drag-to-scroll on pointer devices, native on touch.
        Used by the age nav and collection rails.
     --------------------------------------------------------------------- */
  function initRails() {
    document.querySelectorAll('.cs-rail').forEach(function (rail) {
      if (rail.dataset.csRailReady) return;
      rail.dataset.csRailReady = '1';

      // Past this many pixels a press is a drag rather than a click. The old
      // value was 3, which an ordinary click with a little hand movement clears.
      var SLOP = 8;

      var down = false, startX = 0, startScroll = 0, moved = false, movedAt = 0;

      function scrollable() { return rail.scrollWidth - rail.clientWidth > 1; }

      rail.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'touch') return;   // let iOS/Android do their thing
        if (!scrollable()) return;               // resolved to a grid: nothing to drag
        down = true; moved = false;
        startX = e.clientX;
        startScroll = rail.scrollLeft;

        // .is-dragging is deliberately NOT set here. It carries
        //   .cs-rail.is-dragging a { pointer-events: none }
        // from cs-sections.css, so setting it on press made every link in the
        // rail inert while the button was still down. The mouseup then landed
        // on the <li> instead of the <a>, the click resolved to their common
        // ancestor, and no card in any rail could be clicked on a pointer
        // device. Touch was unaffected, which is what made it look like the
        // links were fine. It goes on below, once the pointer has actually
        // travelled.
      });

      rail.addEventListener('pointermove', function (e) {
        if (!down) return;

        if (!moved) {
          if (Math.abs(e.clientX - startX) <= SLOP) return;
          // Re-anchor on the point the drag really started so the rail does
          // not jump by SLOP on the first frame.
          moved = true;
          startX = e.clientX;
          startScroll = rail.scrollLeft;
          rail.classList.add('is-dragging');
          return;
        }

        movedAt = e.timeStamp;
        rail.scrollLeft = startScroll - (e.clientX - startX);
      });

      ['pointerup', 'pointerleave', 'pointercancel'].forEach(function (evt) {
        rail.addEventListener(evt, function () {
          down = false;
          rail.classList.remove('is-dragging');
        });
      });

      // Suppress only the click that is genuinely the tail of a drag. The flag
      // used to survive a drag that ended off the rail, and then ate the next
      // real click instead.
      rail.addEventListener('click', function (e) {
        if (moved && e.timeStamp - movedAt < 400) {
          e.preventDefault();
          e.stopPropagation();
        }
        moved = false;
      }, true);
    });
  }

  /* ---------------------------------------------------------------------
     Boot + re-run inside the theme editor when a section is re-rendered.
     --------------------------------------------------------------------- */
  function boot() {
    initReveal();
    initImages();
    initRails();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  /* Anything that entered the DOM after the first pass — a lazily rendered
     section, an app injecting markup, a tab panel built on demand — would
     otherwise keep the hidden state forever, because boot() only ever queried
     once. Two cheap safety nets: run again at window load, and then sweep up
     anything still hidden. The sweep is the important one; it is the last
     backstop against a single stalled decode() taking an image with it. */
  window.addEventListener('load', function () {
    boot();
    setTimeout(function () {
      document.querySelectorAll('.cs-reveal:not(.is-in)').forEach(function (el) {
        el.classList.add('is-in');
      });
      document.querySelectorAll('img.cs-fade:not(.is-loaded)').forEach(function (img) {
        img.classList.add('is-loaded');
      });
    }, 2000);
  });

  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:reorder', boot);
})();
